import {FC, useEffect, useState} from 'react'
import {Card, Row, Col, Form, Button, Table} from 'react-bootstrap'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {CGEMetadata, CGESimulateRequest} from '../core/_models'
import RegionDropdown from './RegionDropdown'

const PERM_CGE_HEAVY_COMPUTE = 'PERM_CGE_HEAVY_COMPUTE'

type CGEShockFormProps = {
  metadata: CGEMetadata | null
  onSubmit: (req: CGESimulateRequest) => void
}

type ShockRow = {
  id: number
  good: string
  source: string
  dest: string
  tms_power_change_pct: number
}

type SavedScenario = {
  rows: ShockRow[]
  closure: string
  method: 'johansen' | 'gragg'
}

const SCENARIO_STORAGE_KEY = 'cge-shock-scenario'

let nextRowId = 1
const emptyRow = (): ShockRow => ({
  id: nextRowId++,
  good: '',
  source: '',
  dest: '',
  tms_power_change_pct: 10,
})

const loadSavedScenario = (): SavedScenario | null => {
  try {
    const raw = localStorage.getItem(SCENARIO_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed?.rows) || parsed.rows.length === 0) return null
    return parsed as SavedScenario
  } catch (error) {
    console.error('CGE SCENARIO LOCAL STORAGE PARSE ERROR', error)
    return null
  }
}

const CGEShockForm: FC<CGEShockFormProps> = ({metadata, onSubmit}) => {
  const ability = useAbility(AbilityContext)
  const canRunHeavyCompute = ability.can('approve', PERM_CGE_HEAVY_COMPUTE)
  const [rows, setRows] = useState<ShockRow[]>(() => {
    const saved = loadSavedScenario()
    if (saved) {
      nextRowId = Math.max(...saved.rows.map((r) => r.id), 0) + 1
      return saved.rows
    }
    return [emptyRow()]
  })
  const [closure, setClosure] = useState<string>(() => loadSavedScenario()?.closure ?? 'standard')
  const [method, setMethod] = useState<'johansen' | 'gragg'>(() => {
    const saved = loadSavedScenario()?.method ?? 'johansen'
    return saved === 'gragg' && !canRunHeavyCompute ? 'johansen' : saved
  })

  useEffect(() => {
    try {
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify({rows, closure, method}))
    } catch (error) {
      console.error('CGE SCENARIO LOCAL STORAGE SAVE ERROR', error)
    }
  }, [rows, closure, method])

  const updateRow = (id: number, field: keyof ShockRow, value: string | number) => {
    setRows((prev) => prev.map((r) => (r.id === id ? {...r, [field]: value} : r)))
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])
  const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id))

  const isRowValid = (r: ShockRow) => !!(r.good && r.source && r.dest && r.source !== r.dest)
  const isButtonDisabled = rows.length === 0 || !rows.every(isRowValid)

  const handleSubmit = () => {
    onSubmit({
      bundle_id: metadata?.bundle_id,
      closure,
      method,
      focus: metadata?.focus || 'bgd',
      shocks: rows.map((r) => ({
        good: r.good,
        source: r.source,
        dest: r.dest,
        tms_power_change_pct: r.tms_power_change_pct,
      })),
    })
  }

  return (
    <Card>
      <Card.Header className='card-header-stretch sticky-top bg-white' style={{zIndex: 10}}>
        <Card.Title className='w-100 d-flex justify-content-between align-items-center flex-wrap gap-3'>
          <span>
            <span className='fa fa-gear px-2'></span>
            <h3 className='text-gray-700 d-inline'>Tariff shock scenario</h3>
          </span>
          <span className='d-flex align-items-center gap-2 flex-wrap'>
            <span className='text-primary fw-bold'>Solver:</span>
            <Button
              variant=''
              size='sm'
              className={`fw-bold ${
                method === 'johansen' ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
              }`}
              onClick={() => setMethod('johansen')}
              title='Linear approximation, pre-factorized. Solves in milliseconds'
            >
              johansen
            </Button>
            <Button
              variant=''
              size='sm'
              className={`fw-bold ${
                method === 'gragg' ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
              }`}
              onClick={() => setMethod('gragg')}
              disabled={!canRunHeavyCompute}
              title={
                canRunHeavyCompute
                  ? 'Non-linear, Richardson-extrapolated (2/4/6 steps). Takes roughly 15-60 seconds'
                  : 'Requires additional permission'
              }
            >
              gragg
            </Button>
            <span className='text-primary fw-bold ms-3'>Closure:</span>
            {metadata?.closures.map((c) => (
              <Button
                key={c}
                variant=''
                size='sm'
                className={`fw-bold ${
                  closure === c ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
                }`}
                onClick={() => setClosure(c)}
              >
                {c}
              </Button>
            ))}
          </span>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <div className='table-responsive mb-3'>
          <Table size='sm' className='align-middle'>
            <thead>
              <tr>
                <th>Sector</th>
                <th>Exporting region</th>
                <th>Importing region</th>
                <th>Tariff (tms) power change %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{minWidth: 160}}>
                    <Form.Select
                      size='sm'
                      value={r.good}
                      onChange={(e) => updateRow(r.id, 'good', e.target.value)}
                    >
                      <option value=''>Select sector...</option>
                      {metadata?.sectors.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td style={{minWidth: 180}}>
                    <RegionDropdown
                      options={metadata?.regions ?? []}
                      value={r.source}
                      onChange={(code) => updateRow(r.id, 'source', code)}
                      placeholder='Select region...'
                    />
                  </td>
                  <td style={{minWidth: 180}}>
                    <RegionDropdown
                      options={(metadata?.regions ?? []).filter((reg) => reg.code !== r.source)}
                      value={r.dest}
                      onChange={(code) => updateRow(r.id, 'dest', code)}
                      placeholder='Select region...'
                    />
                  </td>
                  <td style={{minWidth: 120}}>
                    <Form.Control
                      size='sm'
                      type='number'
                      step={0.5}
                      value={r.tms_power_change_pct}
                      onChange={(e) => updateRow(r.id, 'tms_power_change_pct', +e.target.value)}
                    />
                  </td>
                  <td>
                    <Button
                      variant='outline'
                      size='sm'
                      className='text-danger'
                      disabled={rows.length === 1}
                      onClick={() => removeRow(r.id)}
                      title='Remove shock'
                    >
                      <i className='fa-solid fa-trash'></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button variant='outline' className='text-gray-700' size='sm' onClick={addRow}>
            <i className='fa-solid fa-plus me-2'></i>
            Add another sector shock
          </Button>
        </div>

        <Row>
          <Col className='d-flex justify-content-end'>
            <Button
              className='btn btn-warning text-black fw-bold'
              disabled={isButtonDisabled}
              onClick={handleSubmit}
            >
              Run simulation
              <span className='fa-solid fa-angles-right px-1'></span>
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export {CGEShockForm}
